import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { constVoid } from 'fp-ts/function';
import { LocalizedText, FormControls } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { Stack } from '@optimus-web/core';
import { FloatingPanel, FloatingPanelProps } from '@mews/b2b-ui';
import { UnsubmittedChangesDialog } from '@mews-commander/ui';
import { SmsUsaRegistration } from '@mews-commander/data-access';
import { BrandRegistrationFormValues } from './types';
import { BrandRegistrationFormFields } from './BrandRegistrationFormFields';
import { BrandRegistrationDescription } from './BrandRegistrationDescription';

interface BrandRegistrationDialogViewProps extends Pick<FormControls<BrandRegistrationFormValues>, 'submit'> {
    active: boolean;
    isLoading: boolean;
    onCancel: () => void;
    smsUsaRegistration: SmsUsaRegistration;
}

export const BrandRegistrationDialogView = ({
    active,
    isLoading,
    onCancel,
    submit,
    smsUsaRegistration,
}: BrandRegistrationDialogViewProps) => {
    const [unsaved, setUnsaved] = useState(false);
    const { reset, formState } = useFormContext<BrandRegistrationFormValues>();

    useEffect(() => {
        if (!active) {
            reset();
        }
    }, [active, reset]);

    const handleClose = () => {
        if (Object.keys(formState.dirtyFields).length > 0) {
            setUnsaved(true);
            return;
        }

        handleDiscard();
    };

    const handleDiscard = () => {
        setUnsaved(false);
        onCancel();
    };

    const actions: FloatingPanelProps['actions'] = [
        {
            'data-test-id': 'messaging-brand-registration-form-submit',
            children: <LocalizedText textKey={TextKey.Submit} />,
            onClick: submit,
            size: 'medium',
            type: 'submit',
            variant: 'primary',
        },
    ];

    return (
        <>
            <FloatingPanel
                actions={actions}
                active={active}
                loading={isLoading}
                modal
                onClose={isLoading ? constVoid : handleClose}
                size="small"
                title={<LocalizedText textKey={TextKey.CountryUS} />}
            >
                <Stack vertical spacing="400">
                    <BrandRegistrationDescription
                        brandRegistrationCost={smsUsaRegistration.brandRegistrationCost}
                        campaignRegistrationCost={smsUsaRegistration.campaignRegistrationCost}
                    />
                    <BrandRegistrationFormFields termsAndConditionsUrl={smsUsaRegistration.termsAndConditionsUrl} />
                </Stack>
            </FloatingPanel>
            {unsaved ? (
                <UnsubmittedChangesDialog onCancel={() => setUnsaved(false)} onDiscardChanges={handleDiscard} />
            ) : null}
        </>
    );
};
